import type { CashFundGeneralErrorFragment, RegistryErrorResultFragment } from '@graphql/generated';
const UNKNOWN_ERROR_CODE = 'UNKNOWN';

type NormalizableGraphError = CashFundGeneralErrorFragment | RegistryErrorResultFragment;
export interface NormalizedError {
  code: string;
  nativeMessage?: string;
}

/**
 * Normalize structural differences between CashFundGeneralError and RegistryErrorResult
 * Returns null if the result is not an error union member (e.g., success case)
 *
 * @see doc/feature-plans/kyc-failure-messaging.md for implementation details
 */
export function normalizeGraphError(result: NormalizableGraphError | undefined): NormalizedError | null {
  if (!result) return null;

  if (result.__typename === 'CashFundGeneralError') {
    const casted: CashFundGeneralErrorFragment = result;
    return {
      code: casted.code ?? UNKNOWN_ERROR_CODE,
      nativeMessage: casted.nativeMessage ?? undefined,
    };
  }

  if (result.__typename === 'RegistryErrorResult') {
    const casted: RegistryErrorResultFragment = result;
    return {
      code: casted.error?.code ?? UNKNOWN_ERROR_CODE,
      nativeMessage: casted.error?.nativeMessage,
    };
  }

  return null;
}

/**
 * Convert GraphQL error result to JavaScript Error with code as message
 *
 * @see doc/feature-plans/kyc-failure-messaging.md for implementation details
 */
export function errorFromGraphError(result: NormalizableGraphError | undefined): Error {
  const normalized = normalizeGraphError(result);
  return new Error(normalized?.code ?? UNKNOWN_ERROR_CODE);
}
